import { create } from 'zustand'

/**
 * Observable state for the composer's voice controls (2026-08-28 voice
 * composer). PURE STATE — the side effects (mic, transcription WebSocket,
 * manager wiring) live in ONE place, `components/chat/voice/use-voice-session.ts`,
 * mounted once by CompanionHost. Composer buttons call that module's
 * `voiceControls`; this store only tells the UI what to render.
 *
 * - `dictating`: streaming speech-to-text INTO the textarea (mic button).
 * - `voice-session`: a spoken back-and-forth (Speak pill) — STT in, the
 *   manager's voice path + TTS out.
 */
export type VoiceStatus = 'idle' | 'dictating' | 'voice-session'

/** Mirror of the manager's state while a voice session is live. */
export type VoiceSessionState = 'idle' | 'listening' | 'processing' | 'responding'

interface VoiceStoreState {
  status: VoiceStatus
  sessionState: VoiceSessionState
  /** Mic level 0–1 while capturing (dictation or session listening). */
  audioLevel: number
  /** Live interim transcript while capturing. */
  interimText: string
  /** Short message for the session row (e.g. a dropped turn); null when none. */
  notice: string | null

  setStatus: (status: VoiceStatus) => void
  setSessionState: (state: VoiceSessionState) => void
  setAudioLevel: (level: number) => void
  setInterimText: (text: string) => void
  setNotice: (notice: string | null) => void
  reset: () => void
}

export const useVoiceStore = create<VoiceStoreState>((set) => ({
  status: 'idle',
  sessionState: 'idle',
  audioLevel: 0,
  interimText: '',
  notice: null,

  setStatus: (status) => set({ status }),
  setSessionState: (sessionState) => set({ sessionState }),
  setAudioLevel: (audioLevel) => set({ audioLevel }),
  setInterimText: (interimText) => set({ interimText }),
  setNotice: (notice) => set({ notice }),
  reset: () =>
    set({ status: 'idle', sessionState: 'idle', audioLevel: 0, interimText: '', notice: null }),
}))
